// Users never open this dialog: its open state comes only from connection, sync,
// and player state, with no "dismissed" flag.

import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FullScreenOverlay } from '@/components/ui/full-screen-overlay';
import {
  TransitionMessage,
  ROOM_LIFECYCLE,
  ERRORS,
} from '../transitionMessage';
import { useRoomSocket, useRoute } from '../../hooks';
import { analyticsService } from '../../services/analyticsService';
import { ConnectionState } from '@airhead-games/shared/types';

export interface RoomConnectionOverlayProps {
  shouldSyncOnConnection: boolean;
  hasVersionGap: number | null;
  isPlayerInitializing: boolean;
  playerInitializationFailed: boolean;
  isReplayingEvents: boolean;
  isAwaitingRoomState: boolean;
  hasMaxRetryTimeout: boolean;
  onRetryRoomConnection: () => void;
  onClearRoomState?: () => void;
}

const RoomConnectionOverlay: React.FC<RoomConnectionOverlayProps> = ({
  shouldSyncOnConnection,
  hasVersionGap,
  isPlayerInitializing,
  playerInitializationFailed,
  isReplayingEvents,
  isAwaitingRoomState,
  hasMaxRetryTimeout,
  onRetryRoomConnection,
  onClearRoomState,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { connectionState, closeReason, isConnectionReady, clearCloseReason } =
    useRoomSocket();
  const { isHostRoute, isPlayingRoute } = useRoute();

  const handleModalClose = () => {
    analyticsService.trackButtonClick('Ok', {
      link_location: 'room_connect_overlay',
      close_reason: closeReason || 'unknown',
      connection_state: connectionState,
    });
    clearCloseReason();
  };

  const handleRetry = () => {
    analyticsService.trackButtonClick('Retry', {
      link_location: 'room_connect_overlay',
      connection_state: connectionState,
      close_reason: closeReason || 'none',
      player_initialization_failed: playerInitializationFailed,
      has_version_gap: hasVersionGap !== null,
    });
    onRetryRoomConnection();
  };

  const handleExit = () => {
    analyticsService.trackButtonClick('Exit', {
      link_location: 'room_connect_overlay',
      connection_state: connectionState,
      close_reason: closeReason || 'none',
      user_role: isPlayingRoute ? 'player' : 'host',
      navigation_target: isPlayingRoute ? '/join' : '/library',
    });
    onClearRoomState?.();
    navigate(isPlayingRoute ? '/join' : '/library');
  };

  const isConnecting =
    connectionState === ConnectionState.CONNECTING ||
    connectionState === ConnectionState.RECONNECTING;

  const isRoomHydrating =
    shouldSyncOnConnection ||
    hasVersionGap !== null ||
    isPlayerInitializing ||
    isReplayingEvents ||
    isAwaitingRoomState;

  const isConnectionFailure =
    (connectionState === ConnectionState.FAILED ||
      connectionState === ConnectionState.RECONNECT_FAILED ||
      connectionState === ConnectionState.TIMEOUT ||
      (isPlayingRoute && playerInitializationFailed) ||
      hasMaxRetryTimeout) &&
    closeReason !== 'Room closed' &&
    (isHostRoute || isPlayingRoute);

  const isRoomClosedForPlayer =
    closeReason === 'Room closed' &&
    (location.pathname === '/join' || isPlayingRoute);

  const shouldShowFailureModal = isConnectionFailure || isRoomClosedForPlayer;

  const shouldShowLoadingModal =
    !shouldShowFailureModal &&
    (isHostRoute || isPlayingRoute) &&
    (isConnecting || !isConnectionReady || isRoomHydrating);

  if (
    connectionState === ConnectionState.CONNECTED &&
    !shouldShowLoadingModal
  ) {
    return null;
  }

  if (!shouldShowFailureModal && !shouldShowLoadingModal) {
    return null;
  }

  return (
    <FullScreenOverlay
      open={shouldShowFailureModal || shouldShowLoadingModal}
      onOpenChange={(open) => {
        if (!open && isRoomClosedForPlayer) {
          handleModalClose();
        }
      }}
      title={shouldShowFailureModal ? 'Connection problem' : 'Connecting'}
      description={
        shouldShowFailureModal
          ? isRoomClosedForPlayer
            ? 'The room has closed.'
            : 'Could not connect to the room.'
          : 'Connecting to the room.'
      }
      onEscapeKeyDown={(e) => {
        if (!isRoomClosedForPlayer) e.preventDefault();
      }}
      onPointerDownOutside={(e) => e.preventDefault()}
    >
      {shouldShowFailureModal ? (
        <div className="w-full max-w-md px-6">
          <Card className="w-full text-base">
            <CardContent className="flex flex-col gap-6 text-center">
              <TransitionMessage
                messages={
                  isRoomClosedForPlayer
                    ? ROOM_LIFECYCLE.roomClosed
                    : ERRORS.failedToConnect
                }
                layout="inline"
              />
              <div className="flex w-full flex-col gap-3">
                {isRoomClosedForPlayer ? (
                  <Button
                    type="button"
                    onClick={handleModalClose}
                    className="w-full cursor-pointer"
                    size="xl"
                  >
                    OK
                  </Button>
                ) : (
                  <>
                    <Button
                      type="button"
                      onClick={handleExit}
                      className="w-full cursor-pointer"
                      size="xl"
                    >
                      Exit
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleRetry}
                      className="w-full cursor-pointer"
                      size="xl"
                    >
                      Retry
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <TransitionMessage
          messages={ROOM_LIFECYCLE.connectingToRoom}
          showSpinner
          enableFunnyMessages
        />
      )}
    </FullScreenOverlay>
  );
};

export default RoomConnectionOverlay;
